import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, IconButton, Stack } from '@mui/material'
import { PageTitle } from '../components/atoms/PageTitle'
import { Text } from '../components/atoms/Text'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { Button } from '../components/atoms/Button'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { Delete } from '@mui/icons-material'

interface AlertWhitelistEntry {
  id: number
  filter: string[]
  inserted_at: string
}

export function AdminAlertWhitelistPage(): React.ReactElement {

  const { t } = useTranslation()
  const [entries, setEntries] = useState<AlertWhitelistEntry[]>([])
  const [filter, setFilter] = useState('')
  const app = useApp()

  const fetchEntries = () => {
    callAxios<AlertWhitelistEntry[]>({
      url: '/alert-whitelist',
    }).then(res => {
      if (res.success) {
        setEntries(res.data)
      } else {
        app.addError(res)
      }
    })
  }

  const onCreate = () => {
    const parts = filter.split(',').map(s => s.trim()).filter(s => s.length > 0)
    if (parts.length < 2) {
      app.addError(t('Filter must have an operation and at least one argument (e.g. "substring,error text")'))
      return
    }
    callAxios({
      method: 'POST',
      url: '/alert-whitelist',
    }, { filter: parts }).then(res => {
      if (res.success) {
        app.addInfo(t('Whitelist entry created.'))
        setFilter('')
        fetchEntries()
      } else {
        app.addError(res)
      }
    })
  }

  const onDelete = (id: number) => {
    callAxios({
      method: 'DELETE',
      url: `/alert-whitelist/${id}`,
    }).then(res => {
      if (res.success) {
        app.addInfo(t('Whitelist entry deleted.'))
        fetchEntries()
      } else {
        app.addError(res)
      }
    })
  }

  useEffect(() => {
    fetchEntries()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box>
      <PageTitle title={t('Alert Whitelist')}/>

      <PageSubtitle title={t('Add Filter Rule')}/>
      <Box sx={{ mb: 1 }}>
        <Trans>Enter a comma-separated filter rule. First value is the operation (e.g. "substring"), followed by arguments that must match the error message.</Trans>
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ my: 2 }}>
        <Text
          label={t('Filter (e.g. substring,error text,more text)')}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          fullWidth
        />
        <Button onClick={onCreate} disabled={!filter}><Trans>Add</Trans></Button>
      </Stack>

      <PageSubtitle sx={{ mt: 4 }} title={t('Current Whitelist Entries')}/>
      <Box sx={{ my: 2 }}>
        {entries.length === 0 && <Box sx={{ opacity: 0.5 }}><Trans>No whitelist entries.</Trans></Box>}
        {entries.map(entry =>
          <Box
            key={entry.id}
            borderRadius={2}
            padding={1}
            sx={{ border: '1px solid rgba(255,255,255,0.2)', mb: 1 }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '9fr 1fr', alignItems: 'center' }}>
              <Box>
                <Box sx={{ fontFamily: 'monospace' }}>{entry.filter.join(', ')}</Box>
                <Box sx={{ opacity: 0.5, fontSize: '0.85em' }}>{entry.inserted_at}</Box>
              </Box>
              <IconButton onClick={() => onDelete(entry.id)}><Delete sx={{ color: 'error.main' }}/></IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
